import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

import paths from '../../routing/paths';
import accountPaths from '../paths';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/shared/store/rootReducer';
import { loadCmsContent } from '../../cms/actions';
import { CmsCustomTypes } from '../../cms/types';
import { UnknownAction } from 'redux';
import { breakpoints, colors, Divider, durations, opacities } from '../../../design-system';

type Props = {
  isOnline?: boolean;
};

const StyledReturnsContainer = styled.div`
  .paragraph {
    font-size: 1.4rem;
    line-height: 2.1rem;
    margin: 0;
  }

  .step-title {
    margin-bottom: 8px;
  }

  .with-padding {
    padding: 8px 0;
  }

  .returns-title {
    font-size: 2.4rem;
    font-weight: 700;
    margin: 0 0 24px 0;
  }

  .returns-store-title,
  .returns-online-title,
  .returns-explanations {
    font-size: 1.6rem;
    line-height: 2.4rem;
    margin: 0;
    font-weight: 700;
  }

  .returns-store-title {
    margin: 16px 0;
  }

  .returns-online-title,
  .returns-explanations {
    margin: 0 0 16px 0;
  }

  .returns-link {
    margin: 16px 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding: 0 16px;
    width: fit-content;
    min-width: 167px;
    background-color: ${colors.BLACK};
    border-radius: 8px;
    color: ${colors.WHITE};
    font-size: 1.4rem;
    font-weight: 700;
    text-decoration: none;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;
    text-transform: uppercase;

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .returns-explanations-content {
    font-size: 1.4rem;
    line-height: 2.1rem;
    margin: 0 0 24px 0;
  }

  .returns-steps-container {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media (min-width: ${breakpoints.M}px) {
      flex-direction: row;
      gap: 24px;
    }
  }

  .returns-step-container {
    width: 100%;
    margin: 16px 0;

    @media (min-width: ${breakpoints.M}px) {
      width: calc((100% - 72px) / 4);
    }
  }

  ul {
    padding-left: 24px;
  }

  li {
    font-size: 1.4rem;
    line-height: 2.1rem;
    margin: 0;
    padding: 0;
  }

  .returns-customer-services-title {
    margin: 0;
    padding: 8px 0;
    font-size: 1.6rem;
    line-height: 2.4rem;
    font-weight: 700;
  }
`;

export const Returns = ({ isOnline = false }: Props) => {
  const dispatch = useDispatch();

  //  TODO: add order number and autofilled
  //  const { search } = useLocation();
  //  const params = new URLSearchParams(search);
  //  const orderNumber = params.get('orderNumber') ?? '';

  const {
    returns_title,
    returns_subtitle,
    returns_store_title,
    returns_store_text,
    returns_store_button_text,
    returns_online_title,
    returns_online_text,
    returns_online_button_text,
  } = useSelector((state: RootState) => state.cms.account);

  useEffect(() => {
    dispatch(loadCmsContent(CmsCustomTypes.account) as unknown as UnknownAction);
  }, []);

  return isOnline ? (
    <iframe
      src={
        process.env.DEPLOYMENT_ENV === 'production'
          ? 'https://darjeeling.returns.international/'
          : 'https://darjeeling.zigzag-demo.co.uk/'
      }
      height="1000"
      width="100%"
      style={{ border: 0, marginBottom: '32px' }}
    />
  ) : (
    <StyledReturnsContainer id="account-returns-page-container">
      {returns_title && <h2 className="returns-title">{returns_title}</h2>}
      {returns_subtitle && <p className="paragraph">{returns_subtitle}</p>}
      {returns_store_title && <h3 className="returns-store-title">{returns_store_title}</h3>}
      {returns_store_text && <p className="paragraph">{returns_store_text}</p>}
      {returns_store_button_text && (
        <Link data-testid="link-to-store-locator" className="returns-link" to={paths.STORE_LOCATOR}>
          {returns_store_button_text}
        </Link>
      )}
      <Divider my="32px" />
      {returns_online_title && <h3 className="returns-online-title">{returns_online_title}</h3>}
      {returns_online_text && <p className="paragraph">{returns_online_text}</p>}
      {returns_online_button_text && (
        <Link
          data-testid="link-to-online-returns"
          className="returns-link"
          to={`${paths.ACCOUNT + accountPaths.ONLINE_RETURNS}`}
        >
          {returns_online_button_text}
        </Link>
      )}
      <Divider my="32px" />
      <h3 className="returns-explanations">Comment faire un retour ?</h3>
      <p className="paragraph">
        Cliquez sur le bouton « Faire un retour » pour accéder à la plateforme. Suivez les quatre
        étapes, décrites ci-dessous, pour enregistrer votre demande et obtenir votre étiquette de
        retour.”
      </p>
      <div className="returns-steps-container">
        <div className="returns-step-container">
          <h4 className="paragraph step-title">Étape 1</h4>
          <ul>
            <li>Saisissez votre numéro de commande ainsi que votre email ou votre code postal</li>
            <li>Vérifiez vos informations personnelles</li>
            <li>Sélectionnez les articles à retourner</li>
            <li>Indiquez la raison de votre retour</li>
            <li>Choisissez le mode de retour</li>
            <li>Réglez les frais de port</li>
            <li>Téléchargez et imprimez l’étiquette de transport générée</li>
          </ul>
        </div>
        <div className="returns-step-container">
          <h4 className="paragraph step-title">Étape 2</h4>
          <ul>
            <li>Placez les articles à retourner dans un colis</li>
            <li>{`Collez l'étiquette de transport sur le colis`}</li>
          </ul>
        </div>
        <div className="returns-step-container">
          <h4 className="paragraph step-title">Étape 3</h4>
          <p className="paragraph">Déposer votre colis</p>
          <p className="paragraph">Vous avez le choix entre trois options :</p>
          <ul>
            <li>Le retour en boîte aux lettres</li>
            <li>Le dépôt dans votre bureau de poste habituel</li>
            <li>Le dépôt dans un relais mondial relay</li>
          </ul>
        </div>
        <div className="returns-step-container">
          <h4 className="paragraph step-title">Étape 4</h4>
          <ul>
            <li>Dès réception, votre colis sera vérifié et le retour validé</li>
            <li>
              {`Le remboursement est effectué sur le même moyen de paiement que celui ayant servi lors
            de l'achat`}
            </li>
          </ul>
        </div>
      </div>
      <p className="paragraph with-padding">
        Une fois la demande de retour initiée, vous recevrez un email. Vous pourrez alors suivre
        votre retour à tout moment en cliquant sur le lien inclus dans cet email.
      </p>
      <Divider my="24px" />
      <h3 className="returns-customer-services-title"> Service clients</h3>
      <p className="paragraph with-padding">
        {`Besoin d'aide ? Contactez-nous via ce `}
        <a
          data-testid="account-returns-link-to-customer-services"
          className="anchor-animated"
          href="https://faq.darjeeling.fr/hc/fr/requests/new"
          target="_blank"
          rel="noopener noreferrer"
        >
          formulaire
        </a>
      </p>
    </StyledReturnsContainer>
  );
};
